import { alpha } from "@mui/material/styles";
import { chipSemantico } from "../../../utils/centralDeAlertas/cores";

// Colunas da grade da fila: barra de severidade, equipamento+alerta,
// cliente, situação, tempo, quantidade, ação.
const COLUNAS_GRADE =
	"4px minmax(0, 1.7fr) minmax(0, 1fr) 140px 76px 44px 132px";

const styles = (theme) => {
	const escuro = theme.palette.mode === "dark";
	const verde = escuro
		? theme.palette.colors.green.light
		: theme.palette.colors.green.medium;
	const vermelho = escuro
		? theme.palette.colors.red.light
		: theme.palette.colors.red.medium;

	return {
		container: {
			width: "100%",
			height: "100%",
			// Mesmo teto do card de Eventos recentes; a lista rola por dentro.
			maxHeight: "500px",
			borderRadius: "10px",
			pt: "16px",
			display: "flex",
			flexDirection: "column",
			gap: 1.25,
			overflow: "hidden",
		},
		linhaCabecalho: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			gap: 1,
			px: "18px",
		},
		cabecalho: {
			display: "flex",
			alignItems: "center",
			gap: 1,
		},
		badgeTotal: {
			minWidth: "22px",
			px: "8px",
			borderRadius: "999px",
			backgroundColor: vermelho,
			color: theme.palette.background.paper,
			fontSize: "0.75rem",
			fontWeight: 700,
			textAlign: "center",
			lineHeight: "20px",
		},
		campoBusca: {
			width: "220px",
			"& .MuiInputBase-input": { fontSize: "0.8rem", py: "6px" },
		},
		abas: {
			display: "flex",
			flexWrap: "wrap",
			gap: "6px",
			px: "18px",
		},
		aba: (ativa) => ({
			display: "inline-flex",
			alignItems: "center",
			gap: 1,
			height: "30px",
			px: "12px",
			borderRadius: "7px",
			cursor: "pointer",
			font: "inherit",
			fontSize: "0.75rem",
			fontWeight: 500,
			color: ativa ? theme.palette.text.primary : theme.palette.text.secondary,
			backgroundColor: ativa ? theme.palette.action.selected : "transparent",
			border: `1px solid ${ativa ? alpha(theme.palette.text.primary, 0.3) : theme.palette.divider}`,
			"&:hover": { backgroundColor: theme.palette.action.hover },
		}),
		pontoAba: (cor) => ({
			width: "8px",
			height: "8px",
			borderRadius: "2px",
			backgroundColor: cor,
		}),
		totalAba: {
			fontWeight: 700,
			fontVariantNumeric: "tabular-nums",
		},
		cabecalhoGrade: {
			display: "grid",
			gridTemplateColumns: COLUNAS_GRADE,
			gap: 1.5,
			px: "18px",
			py: 1,
			fontSize: "0.65rem",
			color: theme.palette.text.secondary,
			textTransform: "uppercase",
			letterSpacing: "0.06em",
			borderTop: `1px solid ${theme.palette.divider}`,
			borderBottom: `1px solid ${theme.palette.divider}`,
		},
		// Preenche o espaço restante do Paper (altura fixa vem da página).
		lista: {
			display: "flex",
			flexDirection: "column",
			flexGrow: 1,
			minHeight: 0,
			overflowY: "auto",
		},
		linhaGrade: (aberta) => ({
			display: "grid",
			gridTemplateColumns: COLUNAS_GRADE,
			gap: 1.5,
			alignItems: "center",
			px: "18px",
			minHeight: "48px",
			borderBottom: `1px solid ${theme.palette.divider}`,
			backgroundColor: aberta ? alpha(vermelho, escuro ? 0.06 : 0.04) : "transparent",
			"&:hover": { backgroundColor: theme.palette.action.hover },
		}),
		barraSeveridade: (cor) => ({
			width: "4px",
			height: "26px",
			borderRadius: "2px",
			backgroundColor: cor,
		}),
		skeletonItem: {
			width: "calc(100% - 36px)",
			mx: "18px",
			mb: 1,
			minHeight: "40px",
			borderRadius: "8px",
		},
		textosItem: {
			display: "flex",
			flexDirection: "column",
			minWidth: 0,
		},
		tituloItem: {
			fontWeight: 600,
			fontSize: "0.82rem",
			color: theme.palette.text.primary,
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis",
		},
		descricaoItem: {
			color: theme.palette.text.secondary,
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis",
		},
		chipSituacao: (cor) => ({
			...chipSemantico(theme, cor),
			display: "inline-flex",
			alignItems: "center",
			gap: "5px",
			height: "22px",
			px: "8px",
			borderRadius: "6px",
			fontSize: "0.7rem",
			fontWeight: 600,
			whiteSpace: "nowrap",
			"& svg": { fontSize: "14px" },
		}),
		textoNumero: (cor, forte) => ({
			fontSize: "0.78rem",
			color: cor,
			fontWeight: forte ? 700 : 400,
			fontVariantNumeric: "tabular-nums",
			whiteSpace: "nowrap",
		}),
		// Ação primária (zona morta em aberto) cheia; demais discretas.
		botaoAcao: (primario) => ({
			width: "100%",
			textTransform: "none",
			whiteSpace: "nowrap",
			fontSize: "0.72rem",
			fontWeight: 600,
			borderRadius: "6px",
			...(primario
				? {
						color: theme.palette.background.paper,
						backgroundColor: vermelho,
						"&:hover": { backgroundColor: alpha(vermelho, 0.85) },
					}
				: {
						color: theme.palette.text.primary,
						border: `1px solid ${theme.palette.divider}`,
						"&:hover": { backgroundColor: theme.palette.action.selected },
					}),
		}),
		rodape: {
			color: theme.palette.text.secondary,
			px: "18px",
			py: "10px",
			borderTop: `1px solid ${theme.palette.divider}`,
		},
		estadoVazio: {
			flexGrow: 1,
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			gap: 0.5,
			textAlign: "center",
			py: 3,
		},
		iconeVazio: {
			color: verde,
			fontSize: "28px",
		},
		textoVazio: {
			color: theme.palette.text.secondary,
			px: "18px",
			py: 2,
		},
	};
};

export default styles;
